import { getBackground, makeBorderStyles } from '@blaze-shared/elements-domain';
import { type ValueWithUnitOfMeasure } from '@blaze-shared/shared-util';

import { type AudioRecorderPropsModel, type AudioRecorderStyleMode } from './audio-recorder.model';

export const DEFAULT_AUDIO_RECORDER_BUTTON_SIZE = '48px';
export const DEFAULT_AUDIO_RECORDER_ICON_SIZE = '24px';

export const AUDIO_RECORDER_BUTTON_TO_ICON_RATIO = 2;

export function getButtonSizeFromIconSize(
  iconSize: ValueWithUnitOfMeasure | undefined,
): ValueWithUnitOfMeasure {
  const sizeNumber = Number.parseInt(iconSize ?? '', 10);
  if (Number.isNaN(sizeNumber)) {
    return DEFAULT_AUDIO_RECORDER_BUTTON_SIZE;
  }
  return `${Math.round(sizeNumber * AUDIO_RECORDER_BUTTON_TO_ICON_RATIO)}px`;
}

export function buildAudioRecorderButtonStyles(
  button: AudioRecorderPropsModel['style']['button'],
  mode: AudioRecorderStyleMode,
): Record<string, string | undefined> {
  const iconSize = button.iconSize ?? DEFAULT_AUDIO_RECORDER_ICON_SIZE;
  const size = getButtonSizeFromIconSize(button.iconSize);
  const { fill, iconColor } = button[mode];

  return {
    '--icon-size': iconSize,
    background: fill ? getBackground(fill) : undefined,
    color: iconColor ? getBackground(iconColor) : undefined,
    fontSize: iconSize,
    height: size,
    'max-height': 'none',
    'max-width': 'none',
    'min-height': size,
    'min-width': size,
    width: size,
  };
}

export function buildAudioRecorderSectionStyles(
  background: AudioRecorderPropsModel['style']['background'],
  mode: AudioRecorderStyleMode,
): Record<string, string | undefined> {
  const { fill, outline, outlineRadius, outlineWidth } = background[mode];
  if (!fill || !outline) {
    return {};
  }

  const sectionStyles: Record<string, string | undefined> = {
    ...(makeBorderStyles(outline, outlineWidth) ?? {}),
    background: getBackground(fill),
  };

  const hasFillOrOutline = fill.selectorType !== 'none' || outline.selectorType !== 'none';
  if (hasFillOrOutline) {
    sectionStyles['border-radius'] = outlineRadius;
    sectionStyles['padding'] = '12px';
  }

  return sectionStyles;
}
